import { cn } from "@ui/lib";
import type { ComponentProps } from "react";
import {
	type AnimateOptions,
	type Components,
	Streamdown,
	type StreamdownProps,
} from "streamdown";

/**
 * Everything the agent writes, wherever it is shown: the shopper's chat, the
 * recipe steps, the inbox and the playground. Streamdown keeps half-written
 * markdown readable while it streams (an open `**` never flashes as
 * asterisks), fades words in, and picks each block's direction, so an Arabic
 * line reads right to left next to an English one.
 *
 * Replies are one to three sentences over cards that carry the data, so the
 * chrome made for long documents is off: no code or table controls, headings
 * read as bold lines, and images never render (a model or an injected
 * instruction must not put pictures or tracking pixels on screen; product
 * photos come from the cards). Links open straight in a new tab: Streamdown's
 * confirm dialog would not wear the retailer's colours.
 *
 * Streamdown's memo compares object props by identity, so everything passed
 * here lives at module scope, `components` included: build overrides once
 * with `{ ...markdownComponents, ... }` next to the caller, never inline.
 */
export function Markdown({
	children,
	streaming = false,
	className,
	components = markdownComponents,
}: {
	children: string;
	/** True only while this text is still arriving: its words fade in. */
	streaming?: boolean;
	className?: string;
	components?: Components;
}) {
	return (
		<Streamdown
			dir="auto"
			isAnimating={streaming}
			animated={ANIMATION}
			controls={false}
			linkSafety={NO_LINK_DIALOG}
			remend={REMEND}
			disallowedElements={NO_IMAGES}
			components={components}
			className={cn(
				"space-y-2 [&_[data-streamdown=link]]:font-semibold [&_[data-streamdown=strong]]:font-bold",
				className,
			)}
		>
			{children}
		</Streamdown>
	);
}

const ANIMATION: AnimateOptions = {
	animation: "fadeIn",
	sep: "word",
	easing: "var(--ease-out-ui)",
};
const NO_LINK_DIALOG = { enabled: false };
/** A link still being written shows as its text, never as a dead half-URL. */
const REMEND: StreamdownProps["remend"] = { linkMode: "text-only" };
const NO_IMAGES = ["img"];

function Heading({
	node: _node,
	className,
	...props
}: ComponentProps<"p"> & { node?: unknown }) {
	return <p className={cn("font-bold", className)} {...props} />;
}

export const markdownComponents: Components = {
	h1: Heading,
	h2: Heading,
	h3: Heading,
	h4: Heading,
	h5: Heading,
	h6: Heading,
};
